import type { GetUnifiedSubmissionsByFacultyByUserQuery } from "../../../../graphql/generated/graphql";
import { colors } from "../../../../styles/design-system";

export interface GradeBreakdownRow {
  label: string;
  percentage: number | null;
  earned: number | null;
  possible: number | null;
  progressColor: string;
}

type SubmissionQueryData = NonNullable<
  NonNullable<GetUnifiedSubmissionsByFacultyByUserQuery["getUnifiedSubmissionsByFacultyByUser"]>["data"]
>;
export type SubmissionRecord = NonNullable<NonNullable<SubmissionQueryData["submissions"]>[number]>;

export type SubmissionTypeFilter = "All" | "Assignment" | "Assessment";
export type SubmissionStatusFilter = "All" | "Graded" | "Retake Allowed" | "Missing" | "Pending" | "Late";

export interface SubmissionTableRow {
  submission: SubmissionRecord;
  isLate: boolean;
  maxAttempts: number;
  normalizedStatus: Exclude<SubmissionStatusFilter, "All" | "Late">;
  scorePercentage: number | null;
  sortTimestamp: number;
  submissionAttempt: number;
}

export const COURSE_SUBMISSIONS_FETCH_LIMIT = 1000;

export const SUBMISSION_TYPE_OPTIONS: { label: string; value: SubmissionTypeFilter }[] = [
  { label: "All Types", value: "All" },
  { label: "Assignments", value: "Assignment" },
  { label: "Assessments", value: "Assessment" },
];

export const SUBMISSION_STATUS_OPTIONS: { label: string; value: SubmissionStatusFilter }[] = [
  { label: "All Statuses", value: "All" },
  { label: "Graded", value: "Graded" },
  { label: "Retake Allowed", value: "Retake Allowed" },
  { label: "Missing", value: "Missing" },
  { label: "Pending", value: "Pending" },
  { label: "Late", value: "Late" },
];

export const getRoundedPercentage = (earned: number | null, possible: number | null) => {
  if (earned == null || possible == null || possible <= 0) {
    return null;
  }

  return Math.round((earned / possible) * 100);
};

export const getDisplayNumber = (value: number | null) => {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }

  return Number.isInteger(value) ? value.toString() : value.toFixed(1);
};

export const getValueColor = (percentage: number | null, nullColor: string) => {
  if (percentage == null) {
    return nullColor;
  }

  if (percentage < 70) {
    return colors.error[600];
  }

  if (percentage >= 80) {
    return colors.brand[700];
  }

  return colors.grey[900];
};

export const getBreakdownProgressColor = (percentage: number | null) => {
  if (percentage == null) {
    return colors.grey[300];
  }

  if (percentage < 30) {
    return colors.error[600];
  }

  if (percentage > 80) {
    return colors.success[500];
  }

  return colors.warning[500];
};

export const getSubmissionStatus = (submission: SubmissionRecord): SubmissionTableRow["normalizedStatus"] => {
  if (submission.status === "Missing") {
    return "Missing";
  }

  if (submission.status === "Retake Allowed" || submission.status === "RETAKE_ALLOWED") {
    return "Retake Allowed";
  }

  if (submission.status === "Completed" || submission.status === "COMPLETED" || submission.grade != null) {
    return "Graded";
  }

  return "Pending";
};

export const getStatusPillColors = (status: SubmissionTableRow["normalizedStatus"]) => {
  switch (status) {
    case "Graded":
      return {
        backgroundColor: colors.success[50],
        borderColor: colors.success[500],
        textColor: colors.success[700],
      };
    case "Retake Allowed":
      return {
        backgroundColor: colors.blue[50],
        borderColor: colors.blue[600],
        textColor: colors.blue[700],
      };
    case "Missing":
      return {
        backgroundColor: colors.grey[50],
        borderColor: colors.grey[400],
        textColor: colors.grey[700],
      };
    default:
      return {
        backgroundColor: colors.warning[25],
        borderColor: colors.warning[500],
        textColor: colors.warning[700],
      };
  }
};

export const getSubmissionTypeColors = () => {
  return {
    backgroundColor: colors.grey[50],
    iconColor: colors.grey[600],
  };
};

export const getSubmissionSortTimestamp = (submission: SubmissionRecord) => {
  const prioritizedDates = [submission.submittedOn, submission.dueDate, submission.created];

  for (const dateValue of prioritizedDates) {
    if (!dateValue) {
      continue;
    }

    const timestamp = new Date(dateValue).getTime();
    if (Number.isFinite(timestamp)) {
      return timestamp;
    }
  }

  return 0;
};

export const getSubmissionAttemptGroupKey = (submission: SubmissionRecord) =>
  `${submission.courseId}::${submission.moduleName}::${submission.submissionType}::${submission.submissionTitle}`;

export const groupSubmissionsByAttemptKey = (courseSubmissions: SubmissionRecord[]) =>
  courseSubmissions.reduce<Record<string, SubmissionRecord[]>>((groups, submission) => {
    const groupKey = getSubmissionAttemptGroupKey(submission);
    groups[groupKey] = [...(groups[groupKey] ?? []), submission];
    return groups;
  }, {});

const isScoredSubmission = (submission: SubmissionRecord) => {
  return (
    submission.grade != null &&
    submission.maximumPoints != null &&
    Number.isFinite(submission.grade) &&
    Number.isFinite(submission.maximumPoints) &&
    submission.maximumPoints > 0
  );
};

export const sumSubmissionPoints = (
  submissionType: "Assignment" | "Assessment",
  courseSubmissions: SubmissionRecord[]
) => {
  const groupedAttempts = groupSubmissionsByAttemptKey(
    courseSubmissions.filter((submission) => submission.submissionType === submissionType)
  );
  const gradedSubmissions = Object.values(groupedAttempts).flatMap((group) => {
    const latestGradedSubmission = [...group]
      .sort((left, right) => getSubmissionSortTimestamp(right) - getSubmissionSortTimestamp(left))
      .find(isScoredSubmission);

    return latestGradedSubmission ? [latestGradedSubmission] : [];
  });

  const earned = gradedSubmissions.reduce((sum, submission) => sum + (submission.grade ?? 0), 0);
  const possible = gradedSubmissions.reduce((sum, submission) => sum + (submission.maximumPoints ?? 0), 0);

  return {
    earned: gradedSubmissions.length > 0 ? earned : null,
    possible: gradedSubmissions.length > 0 ? possible : null,
  };
};
